/**
 * Portal · 企业壳层状态（租户 / 外观 / 身份）
 * ------------------------------------------------------------
 * 原型对应：Portal 模块（原型 L6341-6450）。
 * - 租户/账号为演示 fixture，非真实登录；切租户不授予角色，仅收窄组织范围。
 * - 外观（主题）按租户生效，写 :root 品牌变量；偏好持久化 localStorage。
 * - 组织数据本体在 scope store（orgRepo）；这里只做「租户 → 组织清单」过滤。
 * - 身份域（多账号 / 在线状态 / 部门挂职 / 个性签名 / 动态 / 本人头像）按账号持久化本机；
 *   概念区分：状态(presence)=当前即时状态；动态(moments)=最近背景情况。
 *   切账号 / 切部门广播 identity:changed，业务侧按新身份重新取数。
 */
import { reactive, computed } from 'vue'
import { emit } from '@/shared/events'

export interface PortalTheme { name: string; header: string; end: string; accent: string; soft: string; line: string }
export interface PortalTenant {
  id: string; name: string; kind: string; orgs: string[]; theme: string
  /** 企业（客户）官网：品牌区点击跳转；M2+ 由后台门户配置按租户下发 */
  site: string
}

/** 官网默认地址（演示域名；正式环境按租户由后台配置覆盖） */
export const DEFAULT_TENANT_SITE = 'https://www.ema-aierp.cn'

export const PORTAL_THEMES: Record<string, PortalTheme> = {
  blue:   { name: '企业蓝', header: '#173a78', end: '#245fbc', accent: '#2f6bff', soft: '#edf2ff', line: '#d3e0ff' },
  green:  { name: '松石绿', header: '#10473f', end: '#197864', accent: '#087d67', soft: '#e7f5f0', line: '#bbdfd4' },
  violet: { name: '典雅紫', header: '#392765', end: '#70509a', accent: '#7551bf', soft: '#f2edfa', line: '#dbcef0' },
  slate:  { name: '石墨灰', header: '#252e3d', end: '#4a586c', accent: '#465a78', soft: '#eef1f6', line: '#cfd8e5' }
}

export const PORTAL_TENANTS: Record<string, PortalTenant> = {
  group:   { id: 'group',   name: '示例集团',       kind: '集团租户',   orgs: ['grp', 'hd', 'hn', 'xn', 'nx'], theme: 'blue',  site: DEFAULT_TENANT_SITE },
  joint:   { id: 'joint',   name: '产销协同企业群', kind: '多企业租户', orgs: ['hd', 'hn'],                    theme: 'green', site: DEFAULT_TENANT_SITE },
  factory: { id: 'factory', name: '华东制造',       kind: '单企业租户', orgs: ['hd'],                          theme: 'slate', site: DEFAULT_TENANT_SITE }
}

/* ============================================================
 * 身份域 · 在线状态（presence，QQ 式即时状态）
 * ============================================================ */
/** 在线状态：online 在线（绿）· busy 忙碌（红）· away 离开（黄）· offline 隐身（灰） */
export type Presence = 'online' | 'busy' | 'away' | 'offline'
export const PRESENCE_LABEL: Record<Presence, string> = {
  online: '在线', busy: '忙碌', away: '离开', offline: '隐身'
}
/** 状态切换面板的展示顺序 */
export const PRESENCE_ORDER: Presence[] = ['online', 'busy', 'away', 'offline']

/* ============================================================
 * 身份域 · 多账号（同时可登录多个账号，直接切换当前登录账号）
 * ============================================================ */
export interface PortalAccount {
  id: string
  /** 真实姓名（HR 主数据口径，不可改；昵称未设置时展示用它兜底） */
  name: string
  initial: string
  /** 账号类型标签 */
  mode: string
  /** 账号主属组织（展示口径） */
  org: string
  /** 主属租户 / 组织 id：切换账号时一并收敛业务范围（租户主题 + 组织数据域） */
  tenant?: string
  orgId?: string
  /** 掩码手机号（演示口径，M2 取账号安全中心） */
  phone: string
  /** 企业邮箱 */
  email: string
  /** 工号（HR 主数据口径，随账号归属主体而异；点击可复制） */
  empNo: string
  /** 挂职部门清单：用户可随时切换当前部门身份 */
  depts: string[]
}

/** 多账号 = 同一员工的本人账号（主 / 子 / 协同），切换只发生在本人账号之间。
 *  约束：对象 key 必须与账号 id 字段一致（switchAccount / byAccount / load 均按 id 索引）。 */
export const PORTAL_ACCOUNTS: Record<string, PortalAccount> = {
  majian: {
    id: 'majian', name: '马剑', initial: '马',
    mode: '主账号', org: '集团总部', tenant: 'group', orgId: 'grp',
    phone: '138****6688', email: 'majian@aierp.demo', empNo: '10001',
    depts: ['数字化与供应链管理部', '集团运营管理部', '产销协同办公室']
  },
  'majian-hd': {
    id: 'majian-hd', name: '马剑', initial: '马',
    mode: '子账号', org: '华东制造基地', tenant: 'factory', orgId: 'hd',
    phone: '138****6688', email: 'majian-hd@aierp.demo', empNo: 'HD-2033',
    depts: ['供应链管理部', '仓储运营部']
  },
  'majian-cs': {
    id: 'majian-cs', name: '马剑', initial: '马',
    mode: '协同账号', org: '产销协同企业群', tenant: 'joint', orgId: 'hd',
    phone: '138****6688', email: 'majian-cs@aierp.demo', empNo: 'CS-1018',
    depts: ['产销协同办公室', '客户成功部']
  }
}

/* ============================================================
 * 身份域 · 动态（moments，最近背景情况，区别于即时状态）
 * ============================================================ */
export interface PortalMoment {
  id: string
  text: string
  /** self=用户自己发布/修改 · system=从系统同步（日程 / 请差旅流程派生） */
  source: 'self' | 'system'
  time: string
}

/** 系统侧可同步的动态种子（M2+ 从日程与 HR 请差旅流程实时派生，此处为演示 fixture） */
const SYSTEM_MOMENTS: string[] = [
  '出差 · 随集团代表团赴华东基地调研，今日乘机途中，消息回复可能延迟',
  '会议中 · 集团月度经营分析会（14:00-16:00），急事请电话联系',
  '请假 · 上午年假，下午恢复在线',
  '驻场 · 在客户现场做系统验收，仅处理紧急事项'
]

/* ============================================================
 * 身份状态与持久化（ema.portal.identity.v1，按账号保存）
 * ============================================================ */
interface IdentityPrefs {
  presence: Presence
  dept?: string
  /** 昵称（未设置 / 留空时展示回落真实姓名） */
  nick?: string
  /** 本人头像（设置中心自定义图片的 dataURL 压缩小图；未设置时全站回落首字占位头像） */
  avatar?: string
  signature: string
  moments: PortalMoment[]
}

const KEY = 'ema.portal.brand.v1'
type TenantPref = { theme?: string; name?: string; site?: string }
const IDENT_KEY = 'ema.portal.identity.v1'
const DEFAULT_SIGNATURE = '让供应链更快、更透明。'
const MOMENT_MAX = 8

const state = reactive({
  tenantId: 'group',
  prefs: {} as Record<string, TenantPref>,
  accountId: 'majian',
  byAccount: {} as Record<string, IdentityPrefs>
})

/** 每个账号的默认身份偏好（含动态种子；签名迁移「我的」页历史值） */
function defaultPrefs(id: string): IdentityPrefs {
  const acc = PORTAL_ACCOUNTS[id]
  const migrated = (() => {
    try { return JSON.parse(localStorage.getItem('ema.profile.v1') || '{}')?.signature } catch { return null }
  })()
  return {
    presence: 'online',
    signature: id === 'majian' && typeof migrated === 'string' && migrated.trim() ? migrated : DEFAULT_SIGNATURE,
    moments: acc ? [{
      id: 'm-seed-1', source: 'self', time: '昨天',
      text: `本周主责${acc.depts[0]}条线事项，周五前在 ${acc.org} 办公`
    }] : []
  }
}

/** 初始化每个账号的偏好槽位（load 后 / 首次访问前保证读取不产生副作用） */
function ensurePrefs() {
  for (const id of Object.keys(PORTAL_ACCOUNTS)) {
    if (!state.byAccount[id]) state.byAccount[id] = defaultPrefs(id)
  }
}
ensurePrefs()

function load() {
  try {
    const p = JSON.parse(localStorage.getItem(KEY) || '{}')
    if (p && typeof p === 'object' && !Array.isArray(p)) {
      if (p.tenantId in PORTAL_TENANTS) state.tenantId = p.tenantId
      if (p.prefs && typeof p.prefs === 'object') state.prefs = p.prefs
    }
  } catch { /* 坏数据视为无偏好 */ }
  try {
    const ident = JSON.parse(localStorage.getItem(IDENT_KEY) || '{}')
    if (ident && typeof ident === 'object' && !Array.isArray(ident)) {
      if (typeof ident.accountId === 'string' && ident.accountId in PORTAL_ACCOUNTS) state.accountId = ident.accountId
      if (ident.byAccount && typeof ident.byAccount === 'object' && !Array.isArray(ident.byAccount)) {
        for (const [id, v] of Object.entries(ident.byAccount as Record<string, IdentityPrefs>)) {
          if (!(id in PORTAL_ACCOUNTS) || !v || typeof v !== 'object') continue
          state.byAccount[id] = { ...state.byAccount[id], ...v }
        }
      }
    }
  } catch { /* 坏数据视为无身份偏好 */ }
  ensurePrefs()
}
function persist() {
  try {
    localStorage.setItem(KEY, JSON.stringify({ tenantId: state.tenantId, prefs: state.prefs }))
    localStorage.setItem(IDENT_KEY, JSON.stringify({ accountId: state.accountId, byAccount: state.byAccount }))
  } catch { /* 忽略 */ }
}

export const portalTenant = computed(() => PORTAL_TENANTS[state.tenantId])
export const portalConfig = computed<TenantPref>(() => state.prefs[state.tenantId] ?? {})
export const portalThemeId = computed(() => {
  const t = portalConfig.value.theme
  return t && PORTAL_THEMES[t] ? t : portalTenant.value.theme
})
export const portalBrandName = computed(() => {
  const n = portalConfig.value.name
  return typeof n === 'string' && n.trim() ? n.trim().slice(0, 30) : portalTenant.value.name
})
/** 企业官网地址：租户配置优先，未配置回落租户默认（品牌区点击跳转用） */
export const portalTenantSite = computed(() => {
  const s = portalConfig.value.site
  return typeof s === 'string' && s.trim() ? s.trim() : portalTenant.value.site
})

/** 写 :root 品牌变量（原型 applyTheme：--brand-* + --accent 三件套） */
function applyTheme() {
  const t = PORTAL_THEMES[portalThemeId.value]
  const root = document.documentElement
  const tokens: Record<string, string> = {
    '--brand-header': t.header,
    '--brand-header-end': t.end,
    '--brand-tint': t.soft,
    '--accent': t.accent,
    '--accent-solid': t.accent,
    '--accent-soft': t.soft,
    '--accent-line': t.line
  }
  Object.entries(tokens).forEach(([k, v]) => root.style.setProperty(k, v))
  root.dataset.portalTheme = portalThemeId.value
}

/** 切租户：收窄组织范围并按租户主题换肤（不在此切组织，由调用方决定回落组织） */
export function setTenant(id: string): boolean {
  if (!PORTAL_TENANTS[id] || id === state.tenantId) return false
  state.tenantId = id
  applyTheme()
  persist()
  return true
}
export function setTheme(id: string): boolean {
  if (!PORTAL_THEMES[id]) return false
  state.prefs[state.tenantId] = { ...state.prefs[state.tenantId], theme: id }
  applyTheme()
  persist()
  return true
}
export function setBrandName(name: string) {
  state.prefs[state.tenantId] = { ...state.prefs[state.tenantId], name: name.slice(0, 30) }
  persist()
}
/** 配置企业官网地址：留空 / 非法协议返回 false 且不落盘（空串清除覆盖，回落租户默认） */
export function setTenantSite(url: string): boolean {
  const v = url.trim()
  if (v && !/^https?:\/\//i.test(v)) return false
  state.prefs[state.tenantId] = { ...state.prefs[state.tenantId], site: v }
  persist()
  return true
}
export function resetAppearance() {
  state.prefs[state.tenantId] = { theme: portalTenant.value.theme, name: portalTenant.value.name }
  applyTheme()
  persist()
}

/* ============================================================
 * 身份域 · 取值器（响应式：切账号 / 切部门后全站展示跟随）
 * ============================================================ */
/** 当前登录账号 */
export const portalAccount = computed<PortalAccount>(
  () => PORTAL_ACCOUNTS[state.accountId] ?? PORTAL_ACCOUNTS.majian
)
/** 当前在线状态（按账号记忆） */
export const portalPresence = computed<Presence>(() => state.byAccount[state.accountId]?.presence ?? 'online')
/** 当前部门挂职（按账号记忆；非法值回落主部门） */
export const portalDept = computed(() => {
  const acc = portalAccount.value
  const d = state.byAccount[state.accountId]?.dept
  return d && acc.depts.includes(d) ? d : acc.depts[0]
})
/** 个性签名（按账号记忆） */
export const portalSignature = computed(() => state.byAccount[state.accountId]?.signature ?? DEFAULT_SIGNATURE)
/** 昵称（默认把真实姓名当昵称；设置后展示昵称） */
export const portalNick = computed(
  () => state.byAccount[state.accountId]?.nick?.trim() || portalAccount.value.name
)
/** 本人头像（设置中心自定义，按账号记忆；未设置时为空串，消费端回落首字占位） */
export const portalAvatar = computed(() => state.byAccount[state.accountId]?.avatar || '')
/** 指定账号的头像（账号切换列表等非当前账号场景用；响应式同 portalAvatar） */
export function accountAvatar(id: string): string {
  return state.byAccount[id]?.avatar || ''
}
/** 动态列表（最近背景情况，最新在前） */
export const portalMoments = computed<PortalMoment[]>(() => state.byAccount[state.accountId]?.moments ?? [])

/* ============================================================
 * 身份域 · 操作（变更即持久化；账号/部门变更广播 identity:changed）
 * ============================================================ */
/** 切换在线状态（在线 / 忙碌 / 离开 / 隐身） */
export function setPresence(p: Presence): boolean {
  if (!PRESENCE_LABEL[p]) return false
  state.byAccount[state.accountId].presence = p
  persist()
  return true
}

/** 切换部门挂职：广播 identity:changed，业务侧按新部门身份重新取数 */
export function setDept(d: string): boolean {
  if (!portalAccount.value.depts.includes(d)) return false
  state.byAccount[state.accountId].dept = d
  persist()
  emit('identity:changed', { type: 'dept', accountId: state.accountId, dept: d })
  return true
}

/** 保存个性签名（用户自己写的一段话） */
export function setSignature(s: string) {
  const v = s.trim().slice(0, 40)
  if (!v) return
  state.byAccount[state.accountId].signature = v
  persist()
}

/** 设置昵称：留空 / 与真实姓名相同则回落真实姓名 */
export function setNick(s: string) {
  const v = s.trim().slice(0, 20)
  const p = state.byAccount[state.accountId]
  if (v && v !== portalAccount.value.name) p.nick = v
  else delete p.nick
  persist()
}

/** 设置本人头像（压缩后的 dataURL 小图）；顶栏 / 聊天 / 讨论 / 个人信息等展示同步跟随 */
export function setAvatar(dataUrl: string) {
  const v = dataUrl.trim()
  if (!v) return
  state.byAccount[state.accountId].avatar = v
  persist()
}
/** 移除自定义头像，全站回落首字占位口径 */
export function resetAvatar() {
  delete state.byAccount[state.accountId].avatar
  persist()
}

/** 发布 / 修改自己的动态（source 保持 self，时间刷新为刚刚） */
export function addMoment(text: string) {
  const v = text.trim().slice(0, 60)
  if (!v) return
  const list = state.byAccount[state.accountId].moments
  list.unshift({ id: 'm-' + Date.now(), text: v, source: 'self', time: '刚刚' })
  state.byAccount[state.accountId].moments = list.slice(0, MOMENT_MAX)
  persist()
}
export function updateMoment(id: string, text: string) {
  const v = text.trim().slice(0, 60)
  if (!v) return
  const list = state.byAccount[state.accountId].moments
  const i = list.findIndex(m => m.id === id)
  if (i >= 0 && list[i].source === 'self') {
    list[i] = { ...list[i], text: v, time: '刚刚' }
    persist()
  }
}
export function removeMoment(id: string) {
  state.byAccount[state.accountId].moments = state.byAccount[state.accountId].moments.filter(m => m.id !== id)
  persist()
}

/** 从系统同步动态：拉取一条未上墙的系统背景（M2+ 实时订阅日程 / 请差旅流程） */
export function syncMoment(): PortalMoment | null {
  const list = state.byAccount[state.accountId].moments
  const text = SYSTEM_MOMENTS.find(t => !list.some(m => m.text === t))
  if (!text) return null
  const m: PortalMoment = { id: 'm-sys-' + Date.now(), text, source: 'system', time: '刚刚' }
  list.unshift(m)
  state.byAccount[state.accountId].moments = list.slice(0, MOMENT_MAX)
  persist()
  return m
}

/** 切换当前登录账号（多账号同时在线，直接切换）；广播 identity:changed */
export function switchAccount(id: string): PortalAccount | null {
  if (!PORTAL_ACCOUNTS[id] || id === state.accountId) return null
  state.accountId = id
  ensurePrefs()
  persist()
  emit('identity:changed', { type: 'account', accountId: id })
  return portalAccount.value
}

export function initPortal() { load(); applyTheme() }
